/**
 * R2 Music Service
 * 
 * Service for fetching and managing music data from Cloudflare R2 bucket
 * 
 * Responsibilities:
 * - Fetch manifest.json from R2 bucket
 * - Transform R2 data to application Track format
 * - Construct full URLs from relative paths
 * - Handle errors and retries
 * - Optional caching
 * 
 * Follows Single Responsibility Principle: Only handles R2 data fetching and transformation
 */

import { nanoid } from 'nanoid';
import { Logger } from '@/app/utils/logger';
import { DataFetchLogger } from '@/app/services/logging/DataFetchLogger';
import { Track, Playlist } from '@/types/MusicPlaylist';
import { R2MusicManifest, R2MusicManifestTrack, R2MusicConfig } from '@/types/R2Music';
import { ConditionalFetchResult } from '@/types/PlaylistCache';
import { getR2BaseUrl } from '@/app/utils/proxyUrl';

export class R2MusicService {
  private config: Required<R2MusicConfig>;
  private cachedData: R2MusicManifest | null = null;
  private cacheTimestamp: number = 0;

  constructor(config: R2MusicConfig) {
    // Use proxy URL in dev, direct URL in prod
    const bucketUrl = config.bucketUrl || import.meta.env.VITE_R2_MUSIC_BUCKET_URL || '';
    const effectiveUrl = getR2BaseUrl();

    // Store the original bucket URL for audio URLs (which need CORS), 
    // but use proxy for API calls
    this.config = {
      bucketUrl: effectiveUrl,
      manifestPath: config.manifestPath ?? config.indexPath ?? 'manifest.json',
      indexPath: config.indexPath ?? 'manifest.json',
      enableCaching: config.enableCaching ?? true,
      cacheDuration: config.cacheDuration ?? 5 * 60 * 1000, // 5 minutes default
    };

    Logger.info(`R2MusicService initialized with bucket URL: ${bucketUrl} (using ${effectiveUrl} for API)`, 'r2-music');
  }

  /**
   * Fetch the manifest.json file from R2 bucket
   * Supports conditional fetching with ETag
   * 
   * @param ifNoneMatch - Optional ETag for conditional request (If-None-Match header)
   * @returns data, etag, and whether response was 304 Not Modified
   */
  private async fetchManifest(ifNoneMatch?: string): Promise<ConditionalFetchResult<R2MusicManifest>> {
    const manifestUrl = `${this.config.bucketUrl}/${this.config.manifestPath}`;
    const startTime = performance.now();

    Logger.info(`Fetching music manifest from: ${manifestUrl}${ifNoneMatch ? ' (conditional)' : ''}`, 'r2-music');

    try {
      const headers: HeadersInit = {
        'Accept': 'application/json',
      };

      // Add If-None-Match header for conditional request
      if (ifNoneMatch) {
        headers['If-None-Match'] = ifNoneMatch;
      }

      const response = await fetch(manifestUrl, {
        method: 'GET',
        headers,
        cache: 'no-cache',
      });

      const etag = response.headers.get('etag') || undefined;
      const durationMs = Math.round(performance.now() - startTime);

      // Handle 304 Not Modified response
      if (response.status === 304) {
        DataFetchLogger.logFetch({
          operation: 'playlist:fetch',
          source: 'r2',
          status: 'success',
          durationMs,
          etag,
        });

        Logger.info('R2 returned 304 Not Modified', 'r2-music');

        return {
          data: null,
          status: 304,
          etag,
          notModified: true,
        };
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch manifest.json: ${response.status} ${response.statusText}`);
      }

      const data: R2MusicManifest = await response.json();
      const totalTracks = Array.isArray(data.tracks) ? data.tracks.length : 0;

      DataFetchLogger.logFetch({
        operation: 'playlist:fetch',
        source: 'r2',
        status: 'success',
        durationMs,
        etag,
        itemCount: totalTracks,
      });

      Logger.info(`Successfully fetched ${totalTracks} tracks from R2`, 'r2-music');

      return {
        data,
        status: response.status,
        etag,
        notModified: false,
      };
    } catch (error) {
      const durationMs = Math.round(performance.now() - startTime);
      const errorMessage = error instanceof Error ? error.message : String(error);

      DataFetchLogger.logFetch({
        operation: 'playlist:fetch',
        source: 'r2',
        status: 'failure',
        durationMs,
        errorMessage,
      });

      Logger.error(`Error fetching music manifest: ${error}`, 'r2-music');
      throw error;
    }
  }

  /**
   * Get music manifest with caching support
   */
  private async getManifest(): Promise<R2MusicManifest> {
    const now = Date.now();
    const cacheAge = this.cachedData !== null ? now - this.cacheTimestamp : 0;
    const isCacheValid =
      this.config.enableCaching &&
      this.cachedData !== null &&
      cacheAge < this.config.cacheDuration;

    if (isCacheValid) {
      const totalTracks = Array.isArray(this.cachedData!.tracks) ? this.cachedData!.tracks.length : 0;

      DataFetchLogger.logFetch({
        operation: 'playlist:fetch',
        source: 'cache',
        status: 'success',
        durationMs: 0,
        cacheHit: true,
        cacheAge,
        itemCount: totalTracks,
      });

      Logger.info('Using cached music index', 'r2-music');
      return this.cachedData!;
    }

    const result = await this.fetchManifest();

    if (result.notModified) {
      // This shouldn't happen in getIndex() since we don't pass etag
      // but handle it anyway by returning cached data
      if (this.cachedData) {
        return this.cachedData;
      }
      throw new Error('Received 304 but no cached data available');
    }

    if (this.config.enableCaching && result.data) {
      this.cachedData = result.data;
      this.cacheTimestamp = now;
    }

    return result.data!;
  }

  /**
   * Fetch playlist with conditional request support (for SWR pattern)
   * This is the public interface for PlaylistFetchService
   * 
   * @param etag - Optional ETag for conditional request
   * @returns ConditionalFetchResult with playlist data
   */
  async fetchPlaylistConditional(etag?: string): Promise<ConditionalFetchResult<Playlist>> {
    const result = await this.fetchManifest(etag);

    // Handle 304 Not Modified - return null data with notModified flag
    if (result.notModified) {
      return {
        data: null,
        status: 304,
        etag: result.etag,
        notModified: true,
      };
    }

    // Transform manifest tracks to Playlist format
    const allTracks = (result.data!.tracks ?? []).map((t) => this.transformTrack(t));
    
    const playlist: Playlist = {
      id: nanoid(),
      name: 'Chill Airwaves Mix',
      description: 'Curated playlist from R2 bucket',
      tracks: allTracks,
      coverUrl: allTracks[0]?.coverUrl,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    return {
      data: playlist,
      status: result.status,
      etag: result.etag,
      notModified: false,
    };
  }

  /**
   * Construct full URL from relative path
   */
  private getFullUrl(relativePath: string): string {
    // Remove leading slash if present
    const cleanPath = relativePath.startsWith('/')
      ? relativePath.slice(1)
      : relativePath;

    return `${this.config.bucketUrl}/${cleanPath}`;
  }

  /**
   * Transform manifest track to application Track format
   */
  private transformTrack(track: R2MusicManifestTrack): Track {
    const coverKey = track.cover_key || track.artist_cover_key || '';

    return {
      id: track.track_id,
      title: track.title,
      artist: track.artist,
      audioUrl: this.getFullUrl(track.audio_key),
      coverUrl: coverKey ? this.getFullUrl(coverKey) : '',
      ...(typeof track.duration_seconds === 'number' ? { duration: track.duration_seconds } : {}),
      metadata: {
      },
    };
  }

  /**
   * Fetch all tracks from R2 and transform to application format
   */
  async fetchAllTracks(): Promise<Track[]> {
    try {
      const manifest = await this.getManifest();
      const allTracks = (manifest.tracks ?? []).map((t) => this.transformTrack(t));

      Logger.info(`Transformed ${allTracks.length} total tracks from R2`, 'r2-music');

      return allTracks;
    } catch (error) {
      Logger.error(`Error fetching tracks: ${error}`, 'r2-music');
      throw error;
    }
  }

  /**
   * Fetch tracks and create a Playlist object
   */
  async fetchPlaylist(
    playlistName: string = 'Chill Airwaves Mix',
    playlistDescription?: string
  ): Promise<Playlist> {
    const tracks = await this.fetchAllTracks();

    const playlist: Playlist = {
      id: nanoid(),
      name: playlistName,
      description: playlistDescription ?? 'Curated playlist from R2 bucket',
      tracks,
      coverUrl: tracks[0]?.coverUrl, // Use first track's cover as playlist cover
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    return playlist;
  }

  /**
   * Fetch tracks grouped by artist
   */
  async fetchTracksByArtist(): Promise<Map<string, Track[]>> {
    const manifest = await this.getManifest();

    const artistMap = new Map<string, Track[]>();

    for (const track of manifest.tracks ?? []) {
      const entry = this.transformTrack(track);
      const existing = artistMap.get(entry.artist) ?? [];
      existing.push(entry);
      artistMap.set(entry.artist, existing);
    }

    return artistMap;
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cachedData = null;
    this.cacheTimestamp = 0;
    Logger.info('R2 music cache cleared', 'r2-music');
  }

  /**
   * Get cache status
   */
  getCacheStatus(): { isCached: boolean; age: number } {
    const age = this.cachedData ? Date.now() - this.cacheTimestamp : 0;
    return {
      isCached: this.cachedData !== null,
      age,
    };
  }
}

/**
 * Singleton instance factory
 */
let r2MusicServiceInstance: R2MusicService | null = null;

export function getR2MusicService(): R2MusicService {
  if (!r2MusicServiceInstance) {
    const bucketUrl = import.meta.env.VITE_R2_MUSIC_BUCKET_URL;

    if (!bucketUrl) {
      throw new Error('VITE_R2_MUSIC_BUCKET_URL environment variable is not set');
    }

    r2MusicServiceInstance = new R2MusicService({
      bucketUrl,
      enableCaching: true,
      cacheDuration: 10 * 60 * 1000, // 10 minutes
    });
  }

  return r2MusicServiceInstance;
}
